import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Switch,
    CircularProgress,
    Alert,
    Card,
    CardContent,
    Chip,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControlLabel,
    MenuItem,
    IconButton,
    Tooltip,
} from "@mui/material";
import {
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    LockReset as LockResetIcon,
} from "@mui/icons-material";
import { apiService } from "../services/api";

interface User {
    id: number;
    login: string;
    name: string | null;
    email: string | null;
    supplierId: number | null;
    clientId: number | null;
    supplier?: { id: number; name: string } | null;
    client?: { id: number; name: string } | null;
    isEnabled: boolean;
    isLoginEnabled: boolean;
    mustChangePassword: boolean;
    comment: string | null;
    createdAt: string;
    updatedAt: string;
}

interface UserFormData {
    login: string;
    password: string;
    name: string;
    email: string;
    supplierId: number | null;
    clientId: number | null;
    isLoginEnabled: boolean;
    isEnabled: boolean;
    mustChangePassword: boolean;
    comment: string;
}

const UsersManagement: React.FC = () => {
    const [users, setUsers] = useState<User[]>([]);
    const [suppliers, setSuppliers] = useState<any[]>([]);
    const [clients, setClients] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [updating, setUpdating] = useState<number | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [resetPasswordDialog, setResetPasswordDialog] = useState<number | null>(null);
    const [newPassword, setNewPassword] = useState("");
    const [formData, setFormData] = useState<UserFormData>({
        login: "",
        password: "",
        name: "",
        email: "",
        supplierId: null,
        clientId: null,
        isLoginEnabled: true,
        isEnabled: true,
        mustChangePassword: false,
        comment: "",
    });

    useEffect(() => {
        fetchUsers();
        fetchSuppliers();
        fetchClients();
    }, []);

    const fetchUsers = async () => {
        setLoading(true);
        setError(null);
        try {
            // Используем all=true для получения всех пользователей без пагинации
            const response = await apiService.getUsers(undefined, true);
            const usersData = Array.isArray(response.data) 
                ? response.data 
                : (response.data?.data || []);
            setUsers(usersData);
        } catch (err: any) {
            setError(err.message || "Ошибка загрузки пользователей");
            setUsers([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchSuppliers = async () => {
        try {
            const suppliersData = await apiService.getSuppliers();
            setSuppliers(suppliersData);
        } catch (err) {
            console.error("Ошибка загрузки поставщиков:", err);
        }
    };

    const fetchClients = async () => {
        try {
            const response = await apiService.getClients(true);
            const clientsData = Array.isArray(response.data) 
                ? response.data 
                : (response.data?.data || []);
            setClients(clientsData);
        } catch (err) {
            console.error("Ошибка загрузки клиентов:", err);
        }
    };

    const handleOpenDialog = (user?: User) => {
        if (user) {
            setEditingUser(user);
            setFormData({
                login: user.login,
                password: "",
                name: user.name || "",
                email: user.email || "",
                supplierId: user.supplierId,
                clientId: user.clientId,
                isLoginEnabled: user.isLoginEnabled,
                isEnabled: user.isEnabled,
                mustChangePassword: user.mustChangePassword,
                comment: user.comment || "",
            });
        } else {
            setEditingUser(null);
            setFormData({
                login: "",
                password: "",
                name: "",
                email: "",
                supplierId: null,
                clientId: null,
                isLoginEnabled: true,
                isEnabled: true,
                mustChangePassword: false,
                comment: "",
            });
        }
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setEditingUser(null);
    };

    const handleSaveUser = async () => {
        try {
            if (editingUser) {
                // Обновление пользователя
                const updateData: any = {
                    login: formData.login,
                    name: formData.name || null,
                    email: formData.email || null,
                    supplierId: formData.supplierId,
                    clientId: formData.clientId,
                    isLoginEnabled: formData.isLoginEnabled,
                    isEnabled: formData.isEnabled,
                    mustChangePassword: formData.mustChangePassword,
                    comment: formData.comment || null,
                };
                if (formData.password) {
                    updateData.password = formData.password;
                }
                await apiService.updateUser(editingUser.id, updateData);
            } else {
                // Создание нового пользователя
                if (!formData.password) {
                    setError("Пароль обязателен для нового пользователя");
                    return;
                }
                await apiService.createUser({
                    login: formData.login,
                    password: formData.password,
                    name: formData.name || null,
                    email: formData.email || null,
                    supplierId: formData.supplierId,
                    clientId: formData.clientId,
                    isLoginEnabled: formData.isLoginEnabled,
                    isEnabled: formData.isEnabled,
                    mustChangePassword: formData.mustChangePassword,
                    comment: formData.comment || null,
                });
            }
            handleCloseDialog();
            fetchUsers();
        } catch (err: any) {
            setError(err.message || "Ошибка сохранения пользователя");
        }
    };

    const handleDeleteUser = async (userId: number) => {
        if (!window.confirm("Вы уверены, что хотите отключить этого пользователя?")) {
            return;
        }
        setUpdating(userId);
        try {
            await apiService.deleteUser(userId);
            fetchUsers();
        } catch (err: any) {
            setError(err.message || "Ошибка удаления пользователя");
        } finally {
            setUpdating(null);
        }
    };

    const handleResetPassword = async () => {
        if (!resetPasswordDialog || !newPassword || newPassword.length < 6) {
            setError("Пароль должен содержать минимум 6 символов");
            return;
        }
        try {
            await apiService.resetUserPassword(resetPasswordDialog, newPassword);
            setResetPasswordDialog(null);
            setNewPassword("");
            fetchUsers();
        } catch (err: any) {
            setError(err.message || "Ошибка сброса пароля");
        }
    };

    const getUserRole = (user: User): string => {
        if (user.supplierId) return `Поставщик: ${user.supplier?.name || user.supplierId}`;
        if (user.clientId) return `Клиент: ${user.client?.name || user.clientId}`;
        return "Администратор";
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4">
                    Управление пользователями
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                >
                    Создать пользователя
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <Card>
                <CardContent>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>ID</TableCell>
                                    <TableCell>Логин</TableCell>
                                    <TableCell>Имя</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Роль</TableCell>
                                    <TableCell align="center">Активен</TableCell>
                                    <TableCell align="center">Вход разрешен</TableCell>
                                    <TableCell align="center">Действия</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {users.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center">
                                            Нет пользователей
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    users.map((user) => (
                                        <TableRow key={user.id}>
                                            <TableCell>{user.id}</TableCell>
                                            <TableCell>
                                                <Typography variant="body1" fontWeight="medium">
                                                    {user.login}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{user.name || "—"}</TableCell>
                                            <TableCell>{user.email || "—"}</TableCell>
                                            <TableCell>
                                                <Chip label={getUserRole(user)} size="small" />
                                            </TableCell>
                                            <TableCell align="center">
                                                {user.isEnabled ? (
                                                    <Chip
                                                        icon={<CheckCircleIcon />}
                                                        label="Да"
                                                        color="success"
                                                        size="small"
                                                    />
                                                ) : (
                                                    <Chip
                                                        icon={<CancelIcon />}
                                                        label="Нет"
                                                        color="error"
                                                        size="small"
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell align="center">
                                                {user.isLoginEnabled ? (
                                                    <Chip
                                                        icon={<CheckCircleIcon />}
                                                        label="Да"
                                                        color="success"
                                                        size="small"
                                                    />
                                                ) : (
                                                    <Chip
                                                        icon={<CancelIcon />}
                                                        label="Нет"
                                                        color="error"
                                                        size="small"
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell align="center">
                                                <Tooltip title="Редактировать">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleOpenDialog(user)}
                                                        color="primary"
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Сбросить пароль">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setResetPasswordDialog(user.id)}
                                                        color="warning"
                                                    >
                                                        <LockResetIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Отключить">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleDeleteUser(user.id)}
                                                        disabled={updating === user.id}
                                                        color="error"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>

            {/* Диалог создания/редактирования пользователя */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    {editingUser ? "Редактировать пользователя" : "Создать пользователя"}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
                        <TextField
                            label="Логин"
                            value={formData.login}
                            onChange={(e) => setFormData({ ...formData, login: e.target.value })}
                            required
                            fullWidth
                        />
                        <TextField
                            label={editingUser ? "Новый пароль (оставьте пустым, чтобы не менять)" : "Пароль"}
                            type="password"
                            value={formData.password}
                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                            required={!editingUser}
                            fullWidth
                        />
                        <TextField
                            label="Имя"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            fullWidth
                        />
                        <TextField
                            label="Email"
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            fullWidth
                        />
                        <TextField
                            select
                            label="Поставщик (оставьте пустым для админа или клиента)"
                            value={formData.supplierId || ""}
                            onChange={(e) => setFormData({ 
                                ...formData, 
                                supplierId: e.target.value ? Number(e.target.value) : null,
                                clientId: e.target.value ? null : formData.clientId,
                            })}
                            fullWidth
                        >
                            <MenuItem value="">—</MenuItem>
                            {suppliers.map((supplier) => (
                                <MenuItem key={supplier.id} value={supplier.id}>
                                    {supplier.name}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            label="Клиент (оставьте пустым для админа или поставщика)"
                            value={formData.clientId || ""}
                            onChange={(e) => setFormData({ 
                                ...formData, 
                                clientId: e.target.value ? Number(e.target.value) : null,
                                supplierId: e.target.value ? null : formData.supplierId,
                            })}
                            fullWidth
                        >
                            <MenuItem value="">—</MenuItem>
                            {clients.map((client) => (
                                <MenuItem key={client.id} value={client.id}>
                                    {client.name}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label="Комментарий"
                            value={formData.comment}
                            onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                            multiline
                            rows={3}
                            fullWidth
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.isEnabled}
                                    onChange={(e) => setFormData({ ...formData, isEnabled: e.target.checked })}
                                />
                            }
                            label="Пользователь активен"
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.isLoginEnabled}
                                    onChange={(e) => setFormData({ ...formData, isLoginEnabled: e.target.checked })}
                                />
                            }
                            label="Разрешен вход в систему"
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.mustChangePassword}
                                    onChange={(e) => setFormData({ ...formData, mustChangePassword: e.target.checked })}
                                />
                            }
                            label="Требуется смена пароля при первом входе"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Отмена</Button>
                    <Button onClick={handleSaveUser} variant="contained" color="primary">
                        Сохранить
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Диалог сброса пароля */}
            <Dialog open={resetPasswordDialog !== null} onClose={() => setResetPasswordDialog(null)}>
                <DialogTitle>Сбросить пароль</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Новый пароль"
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        fullWidth
                        required
                        sx={{ mt: 2 }}
                        helperText="Минимум 6 символов"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResetPasswordDialog(null)}>Отмена</Button>
                    <Button 
                        onClick={handleResetPassword} 
                        variant="contained" 
                        color="primary"
                        disabled={!newPassword || newPassword.length < 6}
                    >
                        Сбросить
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default UsersManagement;

